import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  loginStart,
  loginSuccess,
  loginFailure,
  logout as logoutAction,
  clearError,
  selectAuthLoading,
  selectAuthError,
  selectIsAuthenticated,
  selectUser,
} from '../../app/store/slices/authSlice';
import { resetAll } from '../../app/store/slices/dataSlice';
import { fetchProfile } from '../../app/store/thunks/authThunks';
import apiService from '../services/apiService';
import {API_BASE_URL } from 'constants';


export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Select state from Redux
  const isLoading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);

  // ------------------- Login -------------------
  const login = useCallback(async (email, password) => {
    dispatch(loginStart());

    try {
      const response = await fetch(`${API_BASE_URL}/auth/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, deviceType: "web" }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed");
      }

      const token = data.data.token;
      const userData = data.data.user || {};
      const permissions = userData.permissions || null;
      const centerId = userData.centerId || null;
      const isSuperAdmin = userData.role === 'SUPER_ADMIN' || userData.role === 'ADMIN';

      // Store token and user info in localStorage (for persistence)
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user_email', email);
      sessionStorage.setItem('isLoggedIn', 'true');
      if (permissions) localStorage.setItem('user_permissions', JSON.stringify(permissions));
      if (centerId) localStorage.setItem('user_center_id', centerId);
      if (userData.role) localStorage.setItem('user_role', userData.role);
      localStorage.setItem('is_super_admin', String(isSuperAdmin));

      // Set token in apiService (for legacy compatibility)
      apiService.setToken(token);

      // Dispatch success action to Redux
      dispatch(loginSuccess({
        token,
        user: { email, ...userData },
        permissions,
        centerId,
        isSuperAdmin,
      }));

      dispatch(fetchProfile());

      // Redirect after login
      navigate('/admin/dashboard');

      return data;
    } catch (err) {
      dispatch(loginFailure(err.message || "Login failed. Please try again."));
      return null;
    }
  }, [dispatch, navigate]);

  // ------------------- Logout -------------------
  const logout = useCallback(async () => {
    const token = localStorage.getItem("auth_token");

    try {
      await fetch(`${API_BASE_URL}/auth/admin/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
    } catch (err) {
      console.error('Logout API error:', err);
    }

    // Clear token from apiService
    apiService.clearToken();

    // Dispatch logout action (clears localStorage internally)
    dispatch(logoutAction());

    // Clear all data from Redux
    dispatch(resetAll());

    navigate("/login");
  }, [dispatch, navigate]);

  // ------------------- Signup -------------------
  const signup = useCallback(async (userData) => {
    dispatch(loginStart());

    try {
      const response = await fetch(`${API_BASE_URL}/users/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      dispatch(clearError());
      navigate('/login');
      return data;
    } catch (err) {
      dispatch(loginFailure(err.message || "Registration failed. Please try again."));
      return null;
    }
  }, [dispatch, navigate]);

  // ------------------- Forgot Password -------------------
  const forgotPassword = useCallback(async (email) => {
    dispatch(loginStart());

    try {
      const response = await fetch(`${API_BASE_URL}/auth/admin/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      let data;
      try { data = await response.json(); } catch { data = {}; }

      if (!response.ok) {
        throw new Error(data.message || "Failed to send reset email");
      }

      dispatch(clearError());
      return data;
    } catch (err) {
      dispatch(loginFailure(err.message || "Failed to send reset email"));
      return null;
    }
  }, [dispatch]);

  // ------------------- Reset Password -------------------
  const resetPassword = useCallback(async (token, newPassword) => {
    dispatch(loginStart());

    try {
      const response = await fetch(`${API_BASE_URL}/auth/admin/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Password reset failed");
      }

      dispatch(clearError());
      navigate("/login");
      return data;
    } catch (err) {
      dispatch(loginFailure(err.message || "Password reset failed"));
      return null;
    }
  }, [dispatch, navigate]);

  // ------------------- Clear Error -------------------
  const setError = useCallback((errorMessage) => {
    if (errorMessage) {
      dispatch(loginFailure(errorMessage));
    } else {
      dispatch(clearError());
    }
  }, [dispatch]);

  return {
    login,
    signup,
    forgotPassword,
    resetPassword,
    logout,
    isLoading,
    error,
    setError,
    isAuthenticated,
    user,
  };
};

export default useAuth;
